/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Save, ArrowBack } from "@mui/icons-material";
import {
  getAboutStatsById,
  createAboutStats,
  updateAboutStats,
} from "actions/aboutStats";
import { clearError, clearAboutStat } from "features/aboutStatsSlice";
import { ADMIN_TEXTS } from "constants/adminTexts";
import { PATHNAME } from "constants/pathnames";
import "./About.scss";
import { BrandButton } from "components/brand";

const validationSchema = Yup.object({
  name: Yup.string()
    .required(ADMIN_TEXTS.aboutStats.nameRequired)
    .max(255, ADMIN_TEXTS.aboutStats.nameMaxLength),
  value: Yup.string()
    .required(ADMIN_TEXTS.aboutStats.valueRequired)
    .max(7, ADMIN_TEXTS.aboutStats.valueMaxLength),
});

export const AboutStatsForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const { aboutStat, loading, createLoading, updateLoading, error } =
    useSelector((state) => state.aboutStats);

  const formik = useFormik({
    initialValues: {
      name: "",
      value: "",
    },
    validationSchema,
    onSubmit: (values) => {
      if (isEdit) {
        dispatch(updateAboutStats({ id, data: values }));
      } else {
        dispatch(createAboutStats({ data: values }));
      }
      navigate(PATHNAME.aboutStats);
    },
  });

  const handleBack = () => {
    navigate(PATHNAME.aboutStats);
  };

  useEffect(() => {
    if (isEdit && id) {
      dispatch(getAboutStatsById({ id }));
    }
    return () => {
      dispatch(clearAboutStat());
    };
  }, [id, isEdit]);

  useEffect(() => {
    if (aboutStat && isEdit) {
      formik.setFieldValue("name", aboutStat.name || "");
      formik.setFieldValue("value", aboutStat.value || "");
    }
  }, [aboutStat, isEdit]);

  useEffect(() => {
    if (error) {
      dispatch(clearError());
    }
  }, [error]);

  if (loading) {
    return (
      <Box className="loading-container">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="about-stats-form">
      <Box className="form-header">
        <BrandButton
          variant="secondary"
          startIcon={<ArrowBack />}
          onClick={handleBack}
          className="back-button"
        >
          {ADMIN_TEXTS.common.back}
        </BrandButton>
        <Typography variant="h4" component="h1" fontWeight="bold">
          {isEdit
            ? ADMIN_TEXTS.aboutStats.editStat
            : ADMIN_TEXTS.aboutStats.createStat}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" className="error-alert">
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={ADMIN_TEXTS.aboutStats.nameAsterisk}
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  placeholder={ADMIN_TEXTS.aboutStats.enterName}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={ADMIN_TEXTS.aboutStats.valueAsterisk}
                  name="value"
                  value={formik.values.value}
                  onChange={formik.handleChange}
                  error={formik.touched.value && Boolean(formik.errors.value)}
                  helperText={formik.touched.value && formik.errors.value}
                  placeholder={ADMIN_TEXTS.aboutStats.enterValue}
                />
              </Grid>

              <Grid item xs={12}>
                <Box className="form-actions">
                  <BrandButton
                    variant="secondary"
                    onClick={handleBack}
                    disabled={createLoading || updateLoading}
                  >
                    {ADMIN_TEXTS.common.cancel}
                  </BrandButton>
                  <BrandButton
                    type="submit"
                    variant="primary"
                    startIcon={<Save />}
                    disabled={createLoading || updateLoading}
                  >
                    {createLoading || updateLoading
                      ? ADMIN_TEXTS.actions.saving
                      : ADMIN_TEXTS.common.save}
                  </BrandButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};
